/**
 * PostHog helper for theme code.
 *
 * PostHog itself is loaded by the PixieHog app embed (config/settings_data.json)
 * via {{ content_for_header }} in layout/theme.liquid. Because that embed loads
 * asynchronously, window.posthog may not exist yet when theme scripts run.
 *
 * This helper queues any calls made before PostHog is ready and flushes them
 * once it loads, so callers never have to worry about timing.
 *
 * Usage from anywhere in theme JS:
 *   ThemeAnalytics.track('add_to_cart_clicked', { variant_id: 123, price: 49.9 });
 *   ThemeAnalytics.log('Checkout button rendered');
 *   ThemeAnalytics.logWarn('Inventory low', { variant_id: 123, qty: 2 });
 *   ThemeAnalytics.logError('Cart API failed', { status: 500 });
 *   ThemeAnalytics.identify('user-123', { email: 'a@b.com' });
 */
(function () {
  'use strict';

  var READY_TIMEOUT_MS = 20000;
  var POLL_INTERVAL_MS = 200;

  var queue = [];
  var ready = false;

  function isPosthogReady() {
    return (
      typeof window.posthog !== 'undefined' &&
      window.posthog &&
      typeof window.posthog.capture === 'function' &&
      // The PostHog snippet sets __loaded once the real library is in place.
      // Fall back to capture existing if the flag is unavailable.
      (window.posthog.__loaded === true || typeof window.posthog.__loaded === 'undefined')
    );
  }

  function flushQueue() {
    while (queue.length) {
      var job = queue.shift();
      try {
        job();
      } catch (err) {
        // Never let analytics break the storefront.
        if (window.console && console.error) {
          console.error('[ThemeAnalytics] failed to run queued job', err);
        }
      }
    }
  }

  function runOrQueue(job) {
    if (ready && isPosthogReady()) {
      try {
        job();
      } catch (err) {
        if (window.console && console.error) {
          console.error('[ThemeAnalytics] failed to run job', err);
        }
      }
    } else {
      queue.push(job);
    }
  }

  function waitForPosthog() {
    var elapsed = 0;

    function check() {
      if (isPosthogReady()) {
        ready = true;
        flushQueue();
        return;
      }

      elapsed += POLL_INTERVAL_MS;
      if (elapsed >= READY_TIMEOUT_MS) {
        if (window.console && console.warn) {
          console.warn(
            '[ThemeAnalytics] PostHog did not become available within ' +
              READY_TIMEOUT_MS +
              'ms. Dropping ' +
              queue.length +
              ' queued event(s).'
          );
        }
        queue.length = 0;
        return;
      }

      window.setTimeout(check, POLL_INTERVAL_MS);
    }

    check();
  }

  var ThemeAnalytics = {
    /**
     * Capture a custom PostHog event.
     * @param {string} eventName
     * @param {Object} [properties]
     */
    track: function (eventName, properties) {
      if (!eventName) {
        return;
      }
      var props = properties || {};
      runOrQueue(function () {
        window.posthog.capture(eventName, props);
      });
    },

    /**
     * Send a text log to PostHog as a "theme_log" event.
     * Searchable in PostHog by the `level` and `message` properties.
     * @param {string} message
     * @param {('debug'|'info'|'warn'|'error')} [level='info']
     * @param {Object} [context] extra properties attached to the log
     */
    log: function (message, level, context) {
      if (message === undefined || message === null) {
        return;
      }
      var payload = Object.assign(
        {
          level: level || 'info',
          message: String(message),
          source: 'theme',
          page_path: window.location ? window.location.pathname : undefined,
        },
        context || {}
      );
      runOrQueue(function () {
        window.posthog.capture('theme_log', payload);
      });
    },

    logDebug: function (message, context) {
      this.log(message, 'debug', context);
    },

    logInfo: function (message, context) {
      this.log(message, 'info', context);
    },

    logWarn: function (message, context) {
      this.log(message, 'warn', context);
    },

    logError: function (message, context) {
      this.log(message, 'error', context);
    },

    /**
     * Associate the current session with a user.
     * @param {string} distinctId
     * @param {Object} [properties]
     */
    identify: function (distinctId, properties) {
      if (!distinctId) {
        return;
      }
      var props = properties || {};
      runOrQueue(function () {
        window.posthog.identify(distinctId, props);
      });
    },

    /**
     * Run a callback once PostHog is ready, exposing the raw posthog instance
     * for advanced use cases not covered by the helpers above.
     * @param {(posthog: any) => void} callback
     */
    withPosthog: function (callback) {
      if (typeof callback !== 'function') {
        return;
      }
      runOrQueue(function () {
        callback(window.posthog);
      });
    },
  };

  window.ThemeAnalytics = ThemeAnalytics;

  waitForPosthog();
})();
